/**
 * Cross-tab propagation of "board chat read" state.
 *
 * Marking a board chat / space comment as seen is a fire-and-forget HTTP call that only
 * updates the tab that performed it. Other open tabs keep showing a stale
 * unread indicator until a manual refresh. The reading tab broadcasts the
 * board id and unread count, and every other tab re-emits it onto the in-tab event bus as
 * `board-chat-seen` so components (BoardViewPage, BoardsSidebar, BoardChatMain) can clear their own indicators via
 * `eventBusCallbacks`.
 */
import { createCrossTabChannel } from 'source/lib/crossTabChannel.js';

const channel = createCrossTabChannel('board-chat-seen', 'board-chat-seen');

/**
 * Start listening for cross-tab "board chat read" broadcasts. Called once
 * at app startup so a tab can receive broadcasts even if it never marks a chat
 * as read itself.
 */
export function initBoardChatSeenChannel() {
    channel.init();
}

/**
 * Notify other tabs that the given board's chat has been read.
 *
 * @param {Object} payload
 * @param {number | string} payload.boardId - The board whose chat was seen.
 * @param {number | string} [payload.boardSpaceId] - The specific board space whose chat was seen.
 * @param {number} [payload.delta] - The negative delta of comments seen.
 * @param {number} [payload.unreadCount] - The updated unread count for the board.
 */
export function broadcastBoardChatSeen(payload) {
    if (!payload?.boardId) return;
    channel.broadcast({
        boardId: Number(payload.boardId),
        boardSpaceId: payload.boardSpaceId ? Number(payload.boardSpaceId) : undefined,
        delta: payload.delta,
        unreadCount: payload.unreadCount
    });
}
